/**
 * T1010cr · Lead Scraping & Discovery
 *
 * Mocked merchant-services lead-scraping surface. The operator picks a
 * vertical, geo, and minimum monthly volume, hits "Scrape", and the
 * component simulates a Google Places + local-permits sweep before
 * populating a tabular result set with one row per prospect.  All
 * actions are sandboxed via `useDemoMode` so no DB writes occur.
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import ActiveCardShell from "./ActiveCardShell";
import { Search, MapPin, Loader2, ArrowRight, RefreshCw, Download } from "lucide-react";

const VERTICALS = [
  { id: "restaurants", label: "Restaurants", mcc: "5812" },
  { id: "trades",      label: "Trades / Field",  mcc: "1799" },
  { id: "retail",      label: "Specialty Retail", mcc: "5399" },
  { id: "ecomm",       label: "E-commerce", mcc: "5967" },
  { id: "services",    label: "Professional Services", mcc: "7399" },
];

// Deterministic mocked prospects per vertical — the "scrape" fills
// these in with a short delay to feel like a real API round-trip.
const MOCK_LEADS = {
  restaurants: [
    { name: "Birchwood Grill & Tap",   addr: "812 Harrison Ave, Columbus, OH",  volume: 142000, permit: "2025-11-04", phone: "(614) 555-0117" },
    { name: "Casa Lupita Tacos",       addr: "4411 Henderson Rd, Columbus, OH", volume:  86500, permit: "2026-01-18", phone: "(614) 555-0184" },
    { name: "Marrakesh Lounge",        addr: "77 Vine St, Columbus, OH",        volume: 198000, permit: "2025-09-22", phone: "(614) 555-0191" },
    { name: "Lakeside Oyster House",   addr: "2200 Olentangy, Columbus, OH",    volume: 231500, permit: "2025-12-11", phone: "(614) 555-0135" },
    { name: "Sunrise Diner",           addr: "118 N 4th St, Columbus, OH",      volume:  54300, permit: "2026-02-07", phone: "(614) 555-0128" },
  ],
  trades: [
    { name: "Harlow HVAC & Mechanical",addr: "332 Industrial Pkwy, Dublin, OH", volume:  94500, permit: "2026-01-02", phone: "(614) 555-0203" },
    { name: "RedLine Roofing",         addr: "15 Depot Rd, Westerville, OH",    volume: 173000, permit: "2025-11-29", phone: "(614) 555-0251" },
    { name: "StoneBridge Landscaping", addr: "501 Beech St, Hilliard, OH",      volume:  61200, permit: "2026-01-17", phone: "(614) 555-0266" },
    { name: "Patriot Electrical Co.",  addr: "9 Chamber Ln, Reynoldsburg, OH",  volume: 118900, permit: "2025-10-04", phone: "(614) 555-0279" },
  ],
  retail: [
    { name: "The Vinyl Alcove",        addr: "88 High St, Columbus, OH",        volume:  41200, permit: "2026-01-25", phone: "(614) 555-0312" },
    { name: "Northgate Outfitters",    addr: "6700 Sawmill Rd, Dublin, OH",     volume: 162000, permit: "2025-12-02", phone: "(614) 555-0348" },
    { name: "Daisy Bloom Boutique",    addr: "341 Grandview Ave, OH",           volume:  58900, permit: "2026-02-11", phone: "(614) 555-0355" },
  ],
  ecomm: [
    { name: "Buckeye Bean Co.",        addr: "Fulfillment · Columbus, OH",      volume: 215000, permit: "N/A",         phone: "(614) 555-0410" },
    { name: "Loomtown Textiles",       addr: "Fulfillment · Dublin, OH",        volume:  73400, permit: "N/A",         phone: "(614) 555-0421" },
    { name: "Grove Candle Studio",     addr: "Fulfillment · Hilliard, OH",      volume:  48200, permit: "N/A",         phone: "(614) 555-0433" },
    { name: "Ridgeback Supply",        addr: "Fulfillment · Grove City, OH",    volume: 139800, permit: "N/A",         phone: "(614) 555-0447" },
  ],
  services: [
    { name: "Carson Legal PLLC",       addr: "22 Civic Center, Columbus, OH",   volume:  58900, permit: "2025-10-18", phone: "(614) 555-0514" },
    { name: "Avion Dental Care",       addr: "7811 Riverside, OH",              volume: 102000, permit: "2026-01-09", phone: "(614) 555-0526" },
    { name: "Peak Veterinary Clinic",  addr: "4120 Powell Rd, Powell, OH",      volume:  87400, permit: "2025-11-12", phone: "(614) 555-0538" },
  ],
};

const fmtUsd = (n) => `$${n.toLocaleString()}`;

export default function LeadScrapingCard({ onPushToScoring }) {
  const [vertical, setVertical] = useState("restaurants");
  const [geo, setGeo]           = useState("Columbus, OH");
  const [radius, setRadius]     = useState("25");
  const [minVol, setMinVol]     = useState("40000");
  const [scraping, setScraping] = useState(false);
  const [results, setResults]   = useState([]);

  const scrape = async () => {
    setScraping(true);
    setResults([]);
    // Simulate query time for realism.
    await new Promise((r) => setTimeout(r, 900));
    const raw = MOCK_LEADS[vertical] || [];
    const filtered = raw.filter((l) => l.volume >= Number(minVol || 0));
    setResults(filtered);
    setScraping(false);
    toast.success(`${filtered.length} leads returned · Google Places + permits`);
  };

  const verticalMeta = useMemo(
    () => VERTICALS.find((v) => v.id === vertical),
    [vertical]
  );

  return (
    <ActiveCardShell
      id="T1010cr"
      kicker="LEAD INTELLIGENCE"
      title="Lead Scraping & Discovery"
      Icon={Search}
      actions={
        <>
          <button
            data-testid="veroogle-t1010-export"
            onClick={() => toast.success("Export queued — sandboxed")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono tracking-[0.14em] uppercase text-slate-300 hover:text-white border border-white/10 hover:border-white/30 transition"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            data-testid="veroogle-t1010-scrape"
            onClick={scrape}
            disabled={scraping}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono tracking-[0.14em] uppercase bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-semibold transition disabled:opacity-60"
          >
            {scraping ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scraping…</> : <><RefreshCw className="w-3.5 h-3.5" /> Run Scrape</>}
          </button>
        </>
      }
    >
      {/* Filter row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Vertical</span>
          <select
            data-testid="veroogle-t1010-vertical"
            value={vertical}
            onChange={(e) => setVertical(e.target.value)}
            className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-sm text-white"
          >
            {VERTICALS.map((v) => <option key={v.id} value={v.id}>{`${v.label} · MCC ${v.mcc}`}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Geo</span>
          <div className="relative">
            <MapPin className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              data-testid="veroogle-t1010-geo"
              value={geo}
              onChange={(e) => setGeo(e.target.value)}
              className="w-full rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none pl-8 pr-3 py-2 text-sm text-white"
            />
          </div>
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Radius (mi)</span>
          <input
            data-testid="veroogle-t1010-radius"
            type="number"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-sm text-white"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Min monthly $</span>
          <input
            data-testid="veroogle-t1010-minvol"
            type="number"
            value={minVol}
            onChange={(e) => setMinVol(e.target.value)}
            className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-sm text-white"
          />
        </label>
      </div>

      {/* Results */}
      <div className="mt-5 rounded-xl border border-white/8 overflow-hidden">
        <div className="px-4 py-2 bg-white/[0.02] border-b border-white/8 flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">
            Results · {verticalMeta?.label} · MCC {verticalMeta?.mcc}
          </span>
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-brand-cyan">
            {results.length} leads
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="veroogle-t1010-results">
            <thead>
              <tr className="text-left font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 border-b border-white/8">
                <th className="px-4 py-2 font-normal">Merchant</th>
                <th className="px-4 py-2 font-normal hidden md:table-cell">Address</th>
                <th className="px-4 py-2 font-normal text-right">Est. Monthly</th>
                <th className="px-4 py-2 font-normal hidden lg:table-cell">Permit</th>
                <th className="px-4 py-2 font-normal hidden lg:table-cell">Phone</th>
                <th className="px-4 py-2 font-normal text-right">·</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 && !scraping && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500 text-xs">Run a scrape to populate leads.</td></tr>
              )}
              {scraping && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-brand-cyan text-xs"><Loader2 className="inline w-3.5 h-3.5 animate-spin mr-2" /> Querying Google Places + local permits…</td></tr>
              )}
              {!scraping && results.map((lead, i) => (
                <tr
                  key={i}
                  data-testid={`veroogle-t1010-row-${i}`}
                  className="border-b border-white/5 hover:bg-white/[0.03] transition"
                >
                  <td className="px-4 py-2.5 text-white font-medium">{lead.name}</td>
                  <td className="px-4 py-2.5 text-slate-400 hidden md:table-cell">{lead.addr}</td>
                  <td className="px-4 py-2.5 text-right text-brand-cyan font-mono">{fmtUsd(lead.volume)}</td>
                  <td className="px-4 py-2.5 text-slate-400 hidden lg:table-cell font-mono text-xs">{lead.permit}</td>
                  <td className="px-4 py-2.5 text-slate-400 hidden lg:table-cell font-mono text-xs">{lead.phone}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => onPushToScoring?.(lead)}
                      data-testid={`veroogle-t1010-push-${i}`}
                      className="inline-flex items-center gap-1 text-brand-emerald hover:text-emerald-300 font-mono text-[10px] tracking-[0.14em] uppercase transition"
                    >
                      Push <ArrowRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </ActiveCardShell>
  );
}
